import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, MessageSquare, RotateCcw, ShieldCheck } from 'lucide-react';
import * as clientesApi from '@/api/clientes.api';
import * as installationApi from '@/api/installation.api';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/hooks/useToast';
import { EmptyState } from '@/components/ui/EmptyState';
import { jobStatusBadge, encuestaBadge, leadStatusBadge } from '@/lib/badges';
import { editableLeadFieldsFrom, INTERNAL_NOTES_KEY } from '@/lib/lead-fields';
import { formatPhoneNumber, dateTime } from '@/lib/format';
import { money } from '@/lib/money';
import { Cargando } from '@/components/common/Cargando';

/**
 * Ficha completa de un cliente de la cartera: identidad, lo que el agente
 * capturó de él, total histórico y su historial de trabajos, con el link a la
 * conversación de WhatsApp. Solo lectura.
 */

/** Etiqueta legible de una clave de la tarjeta del lead ("articulo_interes" → "Modelo interes"). */
function humanizeKey(key: string): string {
  const plain = key.replace(/_/g, ' ').trim();
  return plain.charAt(0).toUpperCase() + plain.slice(1);
}

export default function ClienteDetail() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const toast = useToast();
  // Reiniciar la conversación (21-sep-2026, para las pruebas): la asistente
  // vuelve a atender a este cliente como la primera vez.
  const [confirmarReinicio, setConfirmarReinicio] = useState(false);
  const reiniciar = useMutation({
    mutationFn: () => clientesApi.reiniciarConversacion(id!),
    onSuccess: () => {
      setConfirmarReinicio(false);
      void qc.invalidateQueries({ queryKey: ['clientes'] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      toast.success('Conversación reiniciada: el próximo mensaje empieza desde cero');
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'No se pudo reiniciar la conversación'),
  });

  const { data: cliente, isLoading } = useQuery({
    queryKey: ['clientes', 'ficha', id],
    queryFn: () => clientesApi.getClienteFicha(id!),
    enabled: Boolean(id),
  });
  const { data: crmView } = useQuery({
    queryKey: ['installation', 'crm-view'],
    queryFn: installationApi.getCrmView,
  });
  const currencyPrefix = crmView?.currencyPrefix || 'RD$';
  // Igual que en la lista: "total comprado" y el historial salen de TRABAJOS.
  // Sin ese modulo no hay de donde, y dos paneles en cero no informan nada.
  const modInstalaciones = crmView?.osito?.modulos?.instalaciones !== false;
  const ficha = cliente?.osito ?? null;

  if (isLoading) {
    return (
      <Cargando tamano="pantalla" />
    );
  }
  if (!cliente) {
    return (
      <div className="p-6">
        <EmptyState
          title="Cliente no encontrado"
          action={
            <Link to="/clientes">
              <Button variant="outline" size="sm">
                <ArrowLeft className="mr-1 h-4 w-4" /> Volver a Clientes
              </Button>
            </Link>
          }
        />
      </div>
    );
  }

  // Datos que el agente capturó, con las etiquetas de la instalación cuando existen.
  const fieldLabels = new Map(editableLeadFieldsFrom(crmView?.crm).map((f) => [f.key, f.label]));
  const capturado = Object.entries(cliente.leadCard)
    .filter(([key, value]) => key !== INTERNAL_NOTES_KEY && typeof value === 'string' && value.trim())
    .map(([key, value]) => ({
      key,
      label: fieldLabels.get(key) ?? humanizeKey(key),
      value: String(value),
    }));

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={cliente.nombre}
        description={[formatPhoneNumber(cliente.telefono), cliente.zona]
          .filter(Boolean)
          .join(' · ')}
        action={
          <>
            <Link to="/clientes">
              <Button variant="outline" size="sm">
                <ArrowLeft className="mr-1 h-4 w-4" /> Volver
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmarReinicio(true)}
              title="Borra el historial y la ficha: la asistente lo atiende como la primera vez"
            >
              <RotateCcw className="mr-1 h-4 w-4" /> Reiniciar conversación
            </Button>
            {cliente.conversationId && (
              <Link to={`/conversations/${cliente.conversationId}`}>
                <Button size="sm">
                  <MessageSquare className="mr-1 h-4 w-4" /> Ver conversación
                </Button>
              </Link>
            )}
          </>
        }
      />

      <Modal
        open={confirmarReinicio}
        onClose={() => setConfirmarReinicio(false)}
        title="Reiniciar la conversación"
        description={`Se borra todo lo que ${cliente.nombre} habló con la asistente y lo que ella anotó de él. El próximo mensaje que escriba empieza desde cero. No se puede deshacer.`}
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmarReinicio(false)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={() => reiniciar.mutate()} loading={reiniciar.isPending}>
              Sí, reiniciar
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Sirve para probar el guion con el mismo teléfono. El cliente sigue en la lista; lo que se
          borra es su historial.
        </p>
      </Modal>

      {/* Resumen del cliente */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardBody className="flex items-center gap-4">
            <Avatar name={cliente.nombre} src={cliente.avatarUrl} size="lg" />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                {cliente.nombre}
              </p>
              <p className="text-xs text-slate-400">{formatPhoneNumber(cliente.telefono)}</p>
              {cliente.leadStatus && (
                <Badge variant={leadStatusBadge(cliente.leadStatus).variant} className="mt-1">
                  {leadStatusBadge(cliente.leadStatus).label}
                </Badge>
              )}
            </div>
          </CardBody>
        </Card>
        {modInstalaciones ? (
          <Card>
            <CardBody>
              <p className="text-sm text-slate-500 dark:text-slate-400">Total comprado</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                {money(cliente.totalHistorico, currencyPrefix)}
              </p>
              <p className="text-xs text-slate-400">
                {cliente.comprasCount} compra{cliente.comprasCount === 1 ? '' : 's'} en total
              </p>
            </CardBody>
          </Card>
        ) : (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Quién lo atiende</CardTitle>
            </CardHeader>
            <CardBody className="pt-2">
              <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {ficha?.vendedor ?? 'Todavía nadie'}
              </p>
              <p className="text-xs text-slate-400">
                {ficha?.descartadoAt
                  ? `Descartado el ${dateTime(ficha.descartadoAt)} — si vuelve a escribir, sale solo`
                  : (ficha?.intentos ?? 0) > 0
                    ? `${ficha?.intentos} intento${ficha?.intentos === 1 ? '' : 's'} sin respuesta`
                    : 'Sin reenganches pendientes'}
              </p>
              {ficha?.tipoCliente === 'MAYOR' && (
                <Badge variant="info" className="mt-2">
                  Mayorista
                </Badge>
              )}
            </CardBody>
          </Card>
        )}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Lo que sabemos de él</CardTitle>
          </CardHeader>
          <CardBody className="pt-2">
            {capturado.length === 0 ? (
              <p className="text-xs text-slate-400">El agente aún no capturó datos.</p>
            ) : (
              <dl className="space-y-1">
                {capturado.map((f) => (
                  <div key={f.key} className="flex justify-between gap-3 text-sm">
                    <dt className="shrink-0 text-slate-500 dark:text-slate-400">{f.label}</dt>
                    <dd className="truncate text-right text-slate-800 dark:text-slate-200">
                      {f.value}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Historial de trabajos. Solo existe si el negocio hace trabajos: en una
          tienda quedaria un panel con "Sin trabajos todavia" para siempre. */}
      {modInstalaciones && (
      <Card>
        <CardHeader>
          <CardTitle>Historial de trabajos ({cliente.trabajos.length})</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {cliente.trabajos.length === 0 ? (
            <EmptyState
              title="Sin trabajos todavía"
              description="Cuando se le agende un trabajo, aparecerá aquí."
              className="m-5"
            />
          ) : (
            <ul className="divide-y divide-surface-border dark:divide-slate-800">
              {cliente.trabajos.map((t) => (
                <li key={t.id} className="space-y-1 px-5 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-slate-400">{dateTime(t.fecha)}</span>
                    <Badge variant={jobStatusBadge(t.estado).variant}>
                      {jobStatusBadge(t.estado).label}
                    </Badge>
                    {t.esGarantia && (
                      <Badge variant="info">
                        <ShieldCheck className="h-3 w-3" /> Garantía
                      </Badge>
                    )}
                    <span className="ml-auto text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {money(t.precio, currencyPrefix)}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700 dark:text-slate-200">
                    {t.modelo ? `${t.modelo} — ` : ''}
                    {t.descripcion}
                  </p>
                  <p className="text-xs text-slate-400">
                    {[t.tecnico ? `Técnico: ${t.tecnico}` : null, t.resultado]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  {(t.registroArticulo || t.motivo) && (
                    <p className="text-xs text-slate-600 dark:text-slate-300">
                      {[
                        t.registroArticulo ? `Registro del técnico: “${t.registroArticulo}”` : null,
                        t.motivo ? `Motivo: ${t.motivo}` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  )}
                  {t.surveyResponse && t.encuesta && (
                    <p className="flex items-center gap-2 text-xs">
                      <Badge variant={encuestaBadge(t.encuesta).variant}>
                        {encuestaBadge(t.encuesta).label}
                      </Badge>
                      <span className="truncate text-slate-500 dark:text-slate-400">
                        “{t.surveyResponse}”
                      </span>
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
      )}
    </div>
  );
}
